<template>
  <div class="filter-setting-editor">
    <p v-if="parseFailed" class="filter-empty-hint filter-parse-warning">Couldn't simplify the current value automatically — edit the exact stored text below. It won't convert to chips until it fits the "allow all except…" or "only allow…" format.</p>

    <div v-if="!parseFailed" class="filter-main-row">
      <div class="filter-input-col">
        <od_tag_input
          :value="values"
          @input="onValuesInput"
          :placeholder="placeholder"
          variant="secondary"
        />
      </div>

      <div v-if="variant === 'wildcard'" class="filter-controls-col">
        <div class="filter-mode-toggle">
          <label class="filter-mode-option" :class="{ 'filter-mode-option--active': mode === 'exclude' }">
            <input type="radio" :name="radioName" value="exclude" v-model="mode" @change="onModeChange" />
            Allow all except&hellip;
          </label>
          <label class="filter-mode-option" :class="{ 'filter-mode-option--active': mode === 'allow' }">
            <input type="radio" :name="radioName" value="allow" v-model="mode" @change="onModeChange" />
            Only allow&hellip;
          </label>
        </div>
      </div>
    </div>

    <button
      v-if="allowRawEdit && !parseFailed"
      type="button"
      class="filter-raw-toggle"
      @click="toggleRaw"
    >{{ showRaw ? 'Hide raw value' : 'Edit raw value' }}</button>

    <div v-if="showRaw" class="filter-raw-section">
      <b-form-textarea
        v-model="rawDraft"
        rows="2"
        :readonly="!allowRawEdit"
        style="font-family: monospace; font-size: 12px;"
        @input="onRawInput"
      />
      <div class="filter-raw-note">{{ rawNote }}</div>
    </div>
  </div>
</template>

<script>
import od_tag_input from './od_tag_input.vue'
import { parseFilter, serializeFilter, parseList, serializeList } from '../utils/filter_syntax'

let uidCounter = 0

export default {
  name: 'FilterSettingEditor',
  components: { od_tag_input },
  props: {
    value: { type: String, default: '' },
    fieldKey: { type: String, default: '' },
    // 'wildcard' = classic *,-value filter with an allow/exclude mode toggle.
    // 'list' = plain comma list (e.g. a simple skip-list with no * / - semantics).
    variant: { type: String, default: 'wildcard' },
    placeholder: { type: String, default: 'add value and press enter...' },
    allowRawEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      mode: 'exclude',
      values: [],
      showRaw: false,
      rawDraft: '',
      parseFailed: false,
      internalUpdate: false,
      // True when we auto-opened the raw box for an unparseable value, so we
      // know it's safe to auto-close it again once the value parses cleanly
      // (without clobbering a raw box the user opened manually).
      autoRaw: false,
      radioName: 'filter-mode-' + (uidCounter++),
    }
  },
  computed: {
    rawNote() {
      if (!this.allowRawEdit) {
        return 'Raw stored value (read-only) — this is the exact string saved for this field.'
      }
      if (this.parseFailed) {
        return "Raw stored value — this value doesn't fit the chip editor, so edit the exact stored text here."
      }
      return 'Raw stored value — edits here update the field directly and stay in sync with the chips above.'
    },
  },
  created() {
    this.rawDraft = this.value || ''
    this.parseValue(this.value)
  },
  watch: {
    value(newVal) {
      if (this.internalUpdate) return
      this.rawDraft = newVal || ''
      this.parseValue(newVal)
    },
  },
  methods: {
    // Computes { mode, values, parseFailed } from a raw string without
    // touching showRaw/rawDraft — shared by both the chip editor and the
    // raw textarea so the two stay in sync no matter which one changed.
    computeParsed(str) {
      if (this.variant === 'list') {
        return { mode: this.mode, values: parseList(str), parseFailed: false }
      }
      const parsed = parseFilter(str)
      if (parsed.raw !== null) {
        return { mode: this.mode, values: [], parseFailed: true }
      }
      return { mode: parsed.mode, values: parsed.values, parseFailed: false }
    },
    applyParsed(str) {
      const { mode, values, parseFailed } = this.computeParsed(str)
      this.mode = mode
      this.values = values
      this.parseFailed = parseFailed
      this.emitValidation('')
    },
    parseValue(str) {
      this.applyParsed(str)
      // Values that don't fit the chip model have no other way to be seen
      // or edited, so always surface the raw box for them. If the value later
      // parses cleanly, auto-close the box we opened — but never clobber a
      // raw box the user opened manually (autoRaw stays false in that case).
      if (this.parseFailed) {
        this.showRaw = true
        this.autoRaw = true
      } else if (this.autoRaw) {
        this.showRaw = false
        this.autoRaw = false
      }
    },
    onValuesInput(newValues) {
      this.values = newValues
      this.emitUpdate()
    },
    onModeChange() {
      this.emitUpdate()
    },
    emitUpdate() {
      const str = this.variant === 'list'
        ? serializeList(this.values)
        : serializeFilter({ mode: this.mode, values: this.values })
      this.rawDraft = str
      this.internalUpdate = true
      this.$emit('input', str)
      this.$nextTick(() => { this.internalUpdate = false })
    },
    onRawInput(v) {
      this.internalUpdate = true
      this.$emit('input', v)
      const { mode, values, parseFailed } = this.computeParsed(v)
      this.mode = mode
      this.values = values
      this.parseFailed = parseFailed
      this.$nextTick(() => { this.internalUpdate = false })
    },
    toggleRaw() {
      this.showRaw = !this.showRaw
      // User took manual control of the raw box; stop auto-managing it.
      this.autoRaw = false
    },
    emitValidation(error) {
      if (this.fieldKey) {
        this.$emit('validation', this.fieldKey, error)
      }
    },
  },
}
</script>

<style scoped>
.filter-setting-editor {
  max-width: 900px;
}
.filter-main-row {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}
.filter-input-col {
  flex: 1 1 auto;
  min-width: 0;
}
.filter-controls-col {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 6px;
  white-space: nowrap;
}
.filter-mode-toggle {
  display: flex;
  gap: 14px;
}
.filter-mode-option {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
}
.filter-mode-option input {
  margin: 0;
}
.filter-mode-option--active {
  color: #1f5582;
}
.filter-empty-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 4px 0;
  font-style: italic;
}
.filter-parse-warning {
  color: #b45309;
  font-style: normal;
}
.filter-raw-toggle {
  background: transparent;
  border: none;
  padding: 4px 0 0 0;
  font-size: 10.5px;
  color: #1f5582;
  text-decoration: underline;
  cursor: pointer;
  text-align: left;
}
.filter-raw-section {
  margin-top: 8px;
}
.filter-raw-note {
  font-size: 11.5px;
  color: #6b7280;
  margin-top: 3px;
}
</style>
